// File: e1c/src/components/content_editor/ChoiceBlock.js
// REFACTORED: This version is a fully controlled component, removing internal state
// to resolve input responsiveness issues. It now relies entirely on props from its parent.

import React, { useCallback, useMemo } from 'react';
import {
    Paper, TextField, IconButton, Box, Grid, Typography, Button,
    RadioGroup, FormControlLabel, Radio, Checkbox, Tooltip, FormGroup
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

const generateInternalOptionId = () => `temp_opt_id_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

const ChoiceBlock = React.memo(({ id, question, options = {}, onChange, onDelete }) => {

    // Memoize choices derived from props for stability. This becomes the source of truth for rendering.
    const internalChoices = useMemo(() => {
        const choices = Array.isArray(options.choices) ? options.choices : [];
        return choices.map(c => ({
            id: String(c.value || c.id || generateInternalOptionId()), // Ensure a stable key
            label: String(c.label || ''),
            score: c.score !== undefined ? c.score : '',
            goto: c.goto || ''
        }));
    }, [options.choices]);

    const isMultiSelect = useMemo(() => !!options.isMultiSelect, [options.isMultiSelect]);
    const showGotoColumn = true;

    const handleQuestionChange = useCallback((event) => {
        if (onChange) {
            onChange({ question: event.target.value });
        }
    }, [onChange]);

    // Central function to propagate choice array changes to the parent
    const updateChoices = useCallback((newChoices) => {
        if (onChange) {
            // The parent (VisualContentEditor) has special logic to handle this `internalChoices` key
            onChange({ internalChoices: newChoices });
        }
    }, [onChange]);

    const handleOptionChange = useCallback((optionIdToUpdate, field, newValue) => {
        const updatedChoices = internalChoices.map(opt => {
            if (opt.id === optionIdToUpdate) {
                let finalValue = newValue;
                if (field === 'score') {
                    // Allow empty string, otherwise parse as float
                    finalValue = newValue === '' ? '' : parseFloat(newValue);
                    if (isNaN(finalValue)) finalValue = ''; // Revert if not a valid number
                }
                return { ...opt, [field]: finalValue };
            }
            return opt;
        });
        updateChoices(updatedChoices);
    }, [internalChoices, updateChoices]);

    const handleAddOption = useCallback(() => {
        const newOption = { id: generateInternalOptionId(), label: '', score: '', goto: '' };
        updateChoices([...internalChoices, newOption]);
    }, [internalChoices, updateChoices]);

    const handleRemoveOption = useCallback((optionIdToRemove) => {
        let updatedChoices = internalChoices.filter(opt => opt.id !== optionIdToRemove);
        // To maintain a good user experience, ensure at least one option row is always visible
        if (updatedChoices.length === 0) {
            updatedChoices = [{ id: generateInternalOptionId(), label: '', score: '', goto: '' }];
        }
        updateChoices(updatedChoices);
    }, [internalChoices, updateChoices]);

    const handleDeleteClick = useCallback(() => {
        if (onDelete) { onDelete(id); }
    }, [id, onDelete]);

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, position: 'relative', '&:hover .delete-button': { opacity: 1 } }}>
            <IconButton
                className="delete-button" aria-label="delete block" onClick={handleDeleteClick} size="small"
                sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.500', opacity: 0.5, transition: 'opacity 0.2s', '&:hover': { opacity: 1, color: 'error.main' } }}
            >
                <DeleteOutlineIcon fontSize="small"/>
            </IconButton>

            <TextField
                fullWidth variant="outlined" size="small"
                label={`Choice Question Text (ID: ${id})`}
                multiline
                value={question || ''} // Bind value directly to prop
                onChange={handleQuestionChange}
                placeholder="Enter the question for single/multiple choice..." sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" gutterBottom sx={{mb: 1}}>
                Options ({isMultiSelect ? "Multi-Select" : "Single-Select"}):
            </Typography>
             {internalChoices.map((option, index) => (
                <Grid container spacing={1} alignItems="center" key={option.id} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={showGotoColumn ? 5 : 8}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            label={`Option ${index + 1} Label`}
                            value={option.label}
                            onChange={(e) => handleOptionChange(option.id, 'label', e.target.value)}
                            placeholder="Enter option label"
                        />
                    </Grid>


                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            label="Score"
                            value={option.score}
                            onChange={(e) => handleOptionChange(option.id, 'score', e.target.value)}
                            placeholder="e.g., 5"
                        />
                    </Grid>
                    {showGotoColumn && (
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                label="Go to Block ID"
                                value={option.goto}
                                onChange={(e) => handleOptionChange(option.id, 'goto', e.target.value)}
                                placeholder="Optional"
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm="auto">

                        <Tooltip title="Remove this option">
                            <span>
                                <IconButton
                                    onClick={() => handleRemoveOption(option.id)}
                                    size="small"
                                    disabled={internalChoices.length <= 1}
                                >
                                    <RemoveCircleOutlineIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            ))}
            <Button startIcon={<AddCircleOutlineIcon />} onClick={handleAddOption} size="small" sx={{ mt: 1 }}>
                Add Option
            </Button>

            <Box sx={{ mt: 2, p:1, border: '1px dashed lightgrey', borderRadius: 1 }}>
                <Typography variant="caption" display="block" color="text.secondary" sx={{mb:0.5}}>
                    Patient Preview ({isMultiSelect ? "Multi-Select Checkboxes" : "Single-Select Radios"}):
                </Typography>
                {isMultiSelect ? (
                    <FormGroup>
                        {internalChoices.filter(opt => opt.label.trim() !== '').map(opt => (
                            <FormControlLabel key={`preview-mc-${opt.id}`} control={<Checkbox size="small" disabled />} label={opt.label || `(Option)`} disabled />
                        ))}
                    </FormGroup>
                ) : (
                    <RadioGroup>
                        {internalChoices.filter(opt => opt.label.trim() !== '').map(opt => (
                            <FormControlLabel key={`preview-sc-${opt.id}`} value={opt.id} control={<Radio size="small" disabled />} label={opt.label || `(Option)`} disabled />
                        ))}
                    </RadioGroup>
                )}
                {internalChoices.every(opt => opt.label.trim() === '') && internalChoices.length > 0 &&
                    <FormControlLabel value="empty_preview_choice_info" control={isMultiSelect ? <Checkbox size="small" disabled /> : <Radio size="small" disabled />} label="(Define option labels to see preview)" disabled />
                }
                 {internalChoices.length === 0 && <Typography variant="body2" color="text.secondary">(No options defined for preview)</Typography>}

            </Box>
        </Paper>
    );
});

export default ChoiceBlock;